import React from 'react';
import type { DailyPriceTable } from '@/utils/coins/dailyPriceRows';
import { formatTomanPrice } from '@/utils/coins/bubble';
import { faPercent, faUsd, jalaliDate } from '@/utils/gold/mazanehFormat';

interface SymbolDailyTableProps {
    table?: DailyPriceTable;
    symbol: string;
    displayName: string;
}

/** Close, high, low and daily change of the last 30 Tehran days, newest first (server-rendered rows). */
const SymbolDailyTable: React.FC<SymbolDailyTableProps> = ({ table, symbol, displayName }) => {
    const rows = table?.rows || [];
    if (rows.length < 2) return null;

    const usd = (table?.quoteCurrency || '').toUpperCase() === 'USD';
    const unit = usd ? 'دلار' : 'تومان';
    const fmt = (n: number) => (usd ? faUsd(n) : formatTomanPrice(n, symbol));

    return (
        <div className='w-full overflow-x-auto'>
            <table className='w-full min-w-[480px] text-xs lg:text-sm'>
                <caption className='sr-only'>
                    قیمت {displayName} در ۳۰ روز گذشته به {unit}
                </caption>
                <thead>
                    <tr className='border-b border-gray-200 text-zinc-500 dark:border-white/10 dark:text-gray-400'>
                        <th scope='col' className='py-2 text-start font-medium'>
                            تاریخ
                        </th>
                        <th scope='col' className='py-2 text-end font-medium'>
                            قیمت پایانی ({unit})
                        </th>
                        <th scope='col' className='py-2 text-end font-medium'>
                            بیشترین
                        </th>
                        <th scope='col' className='py-2 text-end font-medium'>
                            کمترین
                        </th>
                        <th scope='col' className='py-2 text-end font-medium'>
                            تغییر
                        </th>
                    </tr>
                </thead>
                <tbody className='divide-y divide-gray-100 dark:divide-white/5'>
                    {rows.map((row) => {
                        const change = row.change ?? 0;
                        const changeClass =
                            change > 0
                                ? 'text-green-600 dark:text-green-400'
                                : change < 0
                                  ? 'text-red-600 dark:text-red-400'
                                  : 'text-zinc-500 dark:text-gray-400';
                        return (
                            <tr key={row.day} className='text-zinc-900 dark:text-white'>
                                <td className='py-2 whitespace-nowrap'>
                                    {jalaliDate(new Date(row.day * 1000))}
                                </td>
                                <td className='py-2 text-end font-medium'>
                                    <bdi dir='ltr'>{fmt(row.close)}</bdi>
                                </td>
                                <td className='py-2 text-end'>
                                    <bdi dir='ltr'>{fmt(row.high)}</bdi>
                                </td>
                                <td className='py-2 text-end'>
                                    <bdi dir='ltr'>{fmt(row.low)}</bdi>
                                </td>
                                <td className={`py-2 text-end ${changeClass}`}>
                                    {row.change === null ? (
                                        '—'
                                    ) : (
                                        <bdi dir='ltr'>
                                            {change > 0 ? '+' : change < 0 ? '−' : ''}
                                            {faPercent(change)}٪
                                        </bdi>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default SymbolDailyTable;
